import { useMemo, useState } from "react";
import { Flame, CircleDot, Layers, X, CalendarRange } from "lucide-react";
import { useGeo } from "../../lib/queries";
import { useDateRange } from "../../lib/dateRange";
import { Spinner, EmptyState } from "../../components/ui";
import { SEVERITY_COLOR } from "../../lib/format";
import type { GeoPoint, GeoSite, Severity } from "../../types/api";

// Self-contained SVG geospatial map (no WebGL/deck.gl) so it renders reliably
// in every environment. Heatmap-style density, severity clusters, safety
// choropleth, time-slider and click-to-drill — keyed to incident lat/lng.

const COAST: [number, number][] = [
  [113, -22], [114, -26], [115, -34], [123, -34], [129, -32], [134, -33],
  [138, -35], [140, -38], [145, -38], [148, -38], [150, -37], [153, -32],
  [153, -28], [153, -25], [146, -19], [143, -14], [142, -11], [137, -12],
  [130, -12], [126, -14], [122, -18], [114, -22],
];
const LON0 = 111, LON1 = 155, LAT0 = -9, LAT1 = -45, W = 1000, H = 620;

function project(lon: number, lat: number): [number, number] {
  return [((lon - LON0) / (LON1 - LON0)) * W, ((lat - LAT0) / (LAT1 - LAT0)) * H];
}
function safetyColor(score: number): string {
  const t = Math.max(0, Math.min(1, score / 100));
  const r = Math.round(239 - t * (239 - 45)), g = Math.round(68 + t * (212 - 68)), b = Math.round(68 + t * (191 - 68));
  return `rgb(${r},${g},${b})`;
}

export default function GeoMap() {
  const { data, isLoading } = useGeo();
  // When a global CUSTOM range is active it is the single source of truth for
  // time, so the local scrub slider is hidden to avoid a conflicting/duplicate
  // time filter (the global range already scopes the data via useGeo params).
  const { preset } = useDateRange();
  const customActive = preset === "custom";
  const [layers, setLayers] = useState({ density: true, clusters: true, choropleth: false });
  const [cutoff, setCutoff] = useState(100);
  const [selected, setSelected] = useState<GeoSite | null>(null);

  const points = data?.points ?? [];
  const sites = data?.sites ?? [];

  // While a global custom range is active, don't also apply the local scrub.
  const effCutoff = customActive ? 100 : cutoff;

  const times = useMemo(() => points.map((p) => new Date(p.t).getTime()).filter((n) => !isNaN(n)), [points]);
  const tMin = times.length ? Math.min(...times) : 0;
  const tMax = times.length ? Math.max(...times) : 0;
  const cutoffTs = tMin + ((tMax - tMin) * effCutoff) / 100;

  // Cap density markers for smooth SVG rendering.
  const densityPoints = useMemo(() => {
    const filtered = effCutoff >= 100 ? points : points.filter((p) => new Date(p.t).getTime() <= cutoffTs);
    const step = Math.max(1, Math.ceil(filtered.length / 1500));
    return filtered.filter((_, i) => i % step === 0);
  }, [points, effCutoff, cutoffTs]);

  const path = useMemo(
    () => COAST.map(([lo, la], i) => { const [x, y] = project(lo, la); return `${i === 0 ? "M" : "L"}${x.toFixed(0)},${y.toFixed(0)}`; }).join(" ") + " Z",
    []
  );
  const maxCount = useMemo(() => Math.max(1, ...sites.map((s) => s.count)), [sites]);

  if (isLoading) return <div className="flex h-full items-center justify-center"><Spinner label="Loading geospatial…" /></div>;
  if (!points.length && !sites.length)
    return <div className="flex h-full items-center justify-center"><EmptyState>No geolocated exceptions in range.</EmptyState></div>;

  const toggle = (k: keyof typeof layers) => setLayers((l) => ({ ...l, [k]: !l[k] }));

  return (
    <div className="relative h-full min-h-[280px] w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="land" cx="45%" cy="40%" r="70%">
            <stop offset="0%" stopColor="#12304d" />
            <stop offset="100%" stopColor="#0b1c30" />
          </radialGradient>
          <filter id="soft"><feGaussianBlur stdDeviation="6" /></filter>
        </defs>

        {[0.55, 0.4, 0.25].map((s, i) => (
          <path key={i} d={path} fill="none" stroke="rgba(34,211,238,0.06)" strokeWidth={1}
            transform={`translate(${W / 2} ${H / 2}) scale(${s}) translate(${-W / 2} ${-H / 2})`} />
        ))}
        <path d={path} fill="url(#land)" stroke="rgba(34,211,238,0.25)" strokeWidth={1.5} />

        {/* choropleth: soft safety-score halos per site */}
        {layers.choropleth && sites.map((s) => {
          const [x, y] = project(s.lng, s.lat);
          if (x < 0 || x > W || y < 0 || y > H) return null;
          return <circle key={`c-${s.site}`} cx={x} cy={y} r={26 + Math.sqrt(s.count) * 3}
            fill={safetyColor(s.safety_score)} opacity={0.22} filter="url(#soft)" />;
        })}

        {/* density: translucent severity dots */}
        {layers.density && densityPoints.map((p: GeoPoint, i) => {
          const [x, y] = project(p.lng, p.lat);
          if (x < 0 || x > W || y < 0 || y > H) return null;
          return <circle key={i} cx={x} cy={y} r={2.5 + (p.risk / 100) * 4}
            fill={SEVERITY_COLOR[p.severity as Severity]} opacity={0.28} />;
        })}

        {/* clusters: site circles sized by count, colored by critical ratio */}
        {layers.clusters && sites.map((s) => {
          const [x, y] = project(s.lng, s.lat);
          if (x < 0 || x > W || y < 0 || y > H) return null;
          const ratio = s.count ? s.critical / s.count : 0;
          const color = `rgb(239,${Math.round(140 - ratio * 110)},68)`;
          const r = 6 + Math.sqrt(s.count / maxCount) * 22;
          const hot = ratio > 0.4;
          return (
            <g key={s.site} className="cursor-pointer" onClick={() => setSelected(s)}>
              {hot && <circle cx={x} cy={y} r={r + 8} fill="none" stroke={color} strokeWidth={1} opacity={0.5} className="animate-radar" />}
              <circle cx={x} cy={y} r={r} fill={color} fillOpacity={0.8} stroke="#0A1628" strokeWidth={1.5} />
              <text x={x} y={y + 3} fontSize={10} fill="#0A1628" fontFamily="monospace" textAnchor="middle" fontWeight="bold">{s.count}</text>
              <text x={x + r + 4} y={y + 3} fontSize={11} fill="#94a3b8" fontFamily="monospace">{s.site}</text>
            </g>
          );
        })}
      </svg>

      {/* layer toggles */}
      <div className="absolute left-3 top-3 z-10 flex gap-1.5">
        <Chip on={layers.density} onClick={() => toggle("density")} icon={<Flame size={12} />} label="Density" />
        <Chip on={layers.clusters} onClick={() => toggle("clusters")} icon={<CircleDot size={12} />} label="Clusters" />
        <Chip on={layers.choropleth} onClick={() => toggle("choropleth")} icon={<Layers size={12} />} label="Safety" />
      </div>

      {/* time control — the local scrub slider, unless a global custom range owns time */}
      {customActive ? (
        <div className="absolute bottom-3 left-3 right-3 z-10 flex items-center gap-2 rounded-lg border border-white/10 bg-ocean-900/70 px-3 py-1.5 backdrop-blur">
          <CalendarRange size={13} className="shrink-0 text-aurora-cyan" />
          <span className="text-[10px] text-slatey">
            Time scoped to the custom date range · {densityPoints.length} points
          </span>
        </div>
      ) : (
        <div className="absolute bottom-3 left-3 right-3 z-10 flex items-center gap-2 rounded-lg border border-white/10 bg-ocean-900/70 px-3 py-1.5 backdrop-blur">
          <span className="section-tag whitespace-nowrap">TIME</span>
          <input type="range" min={0} max={100} value={cutoff} onChange={(e) => setCutoff(Number(e.target.value))} className="flex-1 accent-[#22D3EE]" />
          <span className="mono whitespace-nowrap text-[10px] text-slatey">
            {tMax ? new Date(cutoffTs).toLocaleDateString("en-AU", { day: "2-digit", month: "short" }) : "—"} · {densityPoints.length}
          </span>
        </div>
      )}

      {/* legend */}
      <div className="absolute right-3 bottom-12 z-10 rounded-lg border border-white/10 bg-ocean-900/70 px-2 py-1.5 text-[9px] backdrop-blur">
        <div className="section-tag mb-1">SEVERITY</div>
        {(["critical", "high", "medium", "low"] as Severity[]).map((s) => (
          <div key={s} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: SEVERITY_COLOR[s] }} /> {s}</div>
        ))}
      </div>

      {selected && (
        <div className="glass absolute right-3 top-3 z-20 w-52 p-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="section-tag">SITE</span>
            <button onClick={() => setSelected(null)} className="text-slatey hover:text-ink"><X size={14} /></button>
          </div>
          <div className="text-sm font-medium">{selected.site}</div>
          <div className="mt-2 space-y-1 text-xs text-slatey">
            <Row label="Exceptions" value={selected.count} />
            <Row label="Critical" value={selected.critical} />
            <Row label="Safety score" value={`${selected.safety_score}%`} />
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ on, onClick, icon, label }: { on: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1 rounded-lg border px-2 py-1 text-[10px] backdrop-blur transition ${
        on ? "border-aurora-cyan/50 bg-aurora-cyan/15 text-aurora-cyan" : "border-white/10 bg-ocean-900/70 text-slatey"
      }`}>
      {icon} {label}
    </button>
  );
}
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="flex justify-between"><span>{label}</span><span className="mono text-ink">{value}</span></div>;
}
